import { toast } from "sonner";
import { padCh } from "@/components/daw/dawHelpers";

/**
 * iter-278 — Stem-extractor "load / drop audio" flows, extracted verbatim
 * from ELPMStudioDAW (report: oversized component). Returns plain
 * closures — identical semantics to the previous inline handlers.
 *
 * onStemLoadAudio  — iter-215/216 empty-state CTA: open My Songs + arm
 *                    `pendingStemExtract` so routing auto-fires on load.
 * onStemDropAudio  — iter-217/218 drag-and-drop single/batch file(s) →
 *                    load onto free channels → run stem extraction.
 */
export default function useStemDropFlow({
  engine,
  handleUpload,
  handleStemExtract,
  setSelectedChannel,
  setPendingStemExtract,
  setLibraryOpen,
}) {
  const onStemLoadAudio = () => {
    setPendingStemExtract(true);
    setLibraryOpen(true);
    toast.message("Load any song to auto-extract stems", {
      description: "Click 'Load into channel' on the song you want — the routing modal opens automatically after it lands.",
      duration: 6000,
    });
  };

  const onStemDropAudio = async (files) => {
    const fileArr = Array.isArray(files) ? files : [files].filter(Boolean);
    if (fileArr.length === 0) return;
    const isAudio = (f) => f.type?.startsWith("audio/") || /\.(wav|mp3|ogg|m4a|aac|flac|webm)$/i.test(f.name || "");
    const valid = fileArr.filter(isAudio);
    const invalid = fileArr.filter((f) => !isAudio(f));
    if (invalid.length > 0) {
      toast.error(`${invalid.length} file${invalid.length === 1 ? "" : "s"} skipped`, {
        description: invalid.map((f) => f.name).join(", ") + " — drop only WAV / MP3 / OGG / FLAC / M4A.",
      });
    }
    if (valid.length === 0) return;
    // ── Load each file into a fresh free channel in parallel ──
    const freeIdxs = engine.channels
      .map((c, i) => (c.buffer ? null : i))
      .filter((i) => i !== null);
    if (freeIdxs.length < valid.length) {
      toast.error(`Not enough free channels (${freeIdxs.length} free · ${valid.length} requested)`, {
        description: "Free up some channels or drop fewer files.",
      });
      return;
    }
    toast.message(`Loading ${valid.length} file${valid.length === 1 ? "" : "s"}…`, {
      description: valid.map((f, i) => `CH ${padCh(freeIdxs[i])} ← ${f.name}`).join(" · "),
      duration: 6000,
    });
    const loadedChannels = [];
    await Promise.all(
      valid.map(async (file, i) => {
        try {
          await handleUpload(freeIdxs[i], file);
          loadedChannels.push(freeIdxs[i]);
        } catch (e) {
          toast.error(`Could not decode "${file.name}"`, { description: e.message });
        }
      }),
    );
    if (loadedChannels.length === 0) return;
    if (loadedChannels.length === 1) {
      // Single-file path — drop straight into the routing modal (iter-217).
      setSelectedChannel(loadedChannels[0]);
      setTimeout(() => handleStemExtract(), 250);
      return;
    }
    // ── Batch path — action toast triggers sequential extraction. ──
    toast.success(`Loaded ${loadedChannels.length} files`, {
      description: `Click "Extract all" to run stem extraction on each one in turn.`,
      duration: 10000,
      action: {
        label: `Extract all ${loadedChannels.length}`,
        onClick: async () => {
          for (const ch of loadedChannels) {
            setSelectedChannel(ch);
            // Wait briefly so React's selectedChannel state commits
            // before handleStemExtract reads it.
            await new Promise((r) => setTimeout(r, 250));
            handleStemExtract();
            // Wait for THIS extraction to finish before queuing the next.
            await new Promise((resolve) => {
              const id = setInterval(() => {
                if (typeof window !== "undefined" && !document.querySelector('[data-testid="stem-routing-modal"]')) {
                  clearInterval(id);
                  resolve();
                }
              }, 1000);
              // Safety cap so a stuck task can't block the batch (15 min/file).
              setTimeout(() => { clearInterval(id); resolve(); }, 15 * 60_000);
            });
          }
          toast.success(`Batch extraction complete · ${loadedChannels.length} files processed`);
        },
      },
    });
  };

  return { onStemLoadAudio, onStemDropAudio };
}
